import { ArrayHelper } from '../classes/array.helper';
import { PlayerLevel } from '../enums/pitches.enums';
import { DictOf } from '../interfaces/common/i-dictionary';
import { Nullable } from '../interfaces/common/i-generics';
import { IOption } from '../interfaces/common/i-option';
import { IHitter } from '../interfaces/i-hitter';

export enum HitterSide {
  LHH = 'LHH',
  RHH = 'RHH',
}

export const HITTER_SIDES: IOption[] = [
  {
    label: 'constants.hitter-side-lhh',
    value: HitterSide.LHH,
  },
  {
    label: 'constants.hitter-side-rhh',
    value: HitterSide.RHH,
  },
];

export const HITTER_SIDES_DICT = ArrayHelper.firstBy(
  HITTER_SIDES,
  'value'
) as DictOf<IOption>;

export enum HitterSafety {
  high = 'High',
  medium = 'Medium',
  low = 'Low',
}

export const HITTER_SAFETY_BUFFERS: IOption[] = [
  {
    label: 'constants.hitter-safety-high',
    value: HitterSafety.high,
  },
  {
    label: 'constants.hitter-safety-medium',
    value: HitterSafety.medium,
  },
  {
    label: 'constants.hitter-safety-low',
    value: HitterSafety.low,
  },
];

export enum OutcomeCode {
  Ball = 'Ball',
  Single = '1B',
  Double = '2B',
  Triple = '3B',
  HomeRun = 'HR',
  GroundOut = 'GroundOut',
  FlyOut = 'FlyOut',
  FoulOut = 'FO',
  Foul = 'F',
  SwingingStrike = 'S',
  // SwingingStrikeBlocked = 'W',
  CalledStrike = 'C',
  // FoulTip = 'T',
  Unknown = '',
}

// e.g. you can't get a home run without swinging
export const getSwingImpliedByOutcome = (
  outcome: Nullable<OutcomeCode>
): Nullable<boolean> => {
  switch (outcome) {
    case OutcomeCode.Single:
    case OutcomeCode.Double:
    case OutcomeCode.Triple:
    case OutcomeCode.HomeRun:
    case OutcomeCode.FlyOut:
    case OutcomeCode.Foul:
    case OutcomeCode.FoulOut:
    case OutcomeCode.GroundOut:
    case OutcomeCode.SwingingStrike:
      return true;

    case OutcomeCode.Ball:
    case OutcomeCode.CalledStrike:
      return false;

    case OutcomeCode.Unknown:
    case null:
    default:
      return null;
  }
};

// any outcome that cannot possibly result in a detection should suppress detections (e.g. ball cannot have exit velo)
export const getHideHitDetectionsByOutcome = (
  outcome: Nullable<OutcomeCode>
): boolean => {
  switch (outcome) {
    case OutcomeCode.Ball:
    case OutcomeCode.CalledStrike:
    case OutcomeCode.SwingingStrike:
      return true;
    default:
      return false;
  }
};

// what we display to the user
export const MAP_OUTCOME_TO_SHORT_LABEL: Record<OutcomeCode, string> = {
  [OutcomeCode.Ball]: 'Ball',
  [OutcomeCode.CalledStrike]: 'Called Strike',
  [OutcomeCode.Double]: '2B',
  [OutcomeCode.FlyOut]: 'Fly Out',
  [OutcomeCode.Foul]: 'Foul',
  [OutcomeCode.FoulOut]: 'Foul',
  // [OutcomeCode.FoulTip]: 'Foul Tip',
  [OutcomeCode.GroundOut]: 'Ground Out',
  [OutcomeCode.HomeRun]: 'HR',
  [OutcomeCode.Single]: '1B',
  [OutcomeCode.SwingingStrike]: 'Swinging Strike',
  // [OutcomeCode.SwingingStrikeBlocked]: 'Swinging Strike (Blocked)',
  [OutcomeCode.Triple]: '3B',
  [OutcomeCode.Unknown]: '',
};

export const MAP_OUTCOME_TO_PRETTY_LABEL: Record<OutcomeCode, string> = {
  [OutcomeCode.Ball]: 'Ball',
  [OutcomeCode.CalledStrike]: 'Called Strike',
  [OutcomeCode.Double]: 'Double',
  [OutcomeCode.FlyOut]: 'Fly Out',
  [OutcomeCode.Foul]: 'Foul',
  [OutcomeCode.FoulOut]: 'Foul Out',
  // [OutcomeCode.FoulTip]: 'Foul Tip',
  [OutcomeCode.GroundOut]: 'Ground Out',
  [OutcomeCode.HomeRun]: 'Home Run',
  [OutcomeCode.Single]: 'Single',
  [OutcomeCode.SwingingStrike]: 'Swinging Strike',
  // [OutcomeCode.SwingingStrikeBlocked]: 'Swinging Strike (Blocked)',
  [OutcomeCode.Triple]: 'Triple',
  [OutcomeCode.Unknown]: '',
};

// for filter options/table ordering
export const HIT_OUTCOME_PRIORITY: OutcomeCode[] = [
  OutcomeCode.HomeRun,
  OutcomeCode.Triple,
  OutcomeCode.Double,
  OutcomeCode.Single,
  OutcomeCode.FlyOut,
  OutcomeCode.FoulOut,
  OutcomeCode.GroundOut,
  OutcomeCode.Foul,
  // OutcomeCode.FoulTip,
  OutcomeCode.CalledStrike,
  OutcomeCode.SwingingStrike,
  // OutcomeCode.SwingingStrikeBlocked,
  OutcomeCode.Ball,
  OutcomeCode.Unknown,
];

export const MIN_HR_DISTANCE_FT = 320;

export const MAX_FOUL_H_LAUNCH_DEG = 45;

export enum HitterDataSource {
  StatsApi = 'Stats API',
  Manual = 'Manual',
  Csv = 'CSV',
}

export enum HitterRosterStatus {
  OnRoster = 'On Roster',
  OffRoster = 'Off Roster',
  NA = 'N/A',
}

export enum HitterProfilePeriod {
  Last7Days = 'constants.last-7-days',
  Last1Month = 'constants.last-1-month',
  Last3Months = 'constants.last-3-months',
  Last6Months = 'constants.last-6-months',
  YTD = 'constants.ytd',
  Last1Year = 'constants.last-1-year',
}

export enum HitModel {
  V6_2 = 'hit-6.2', // ! deprecated
  V6_3 = 'hit-6.3', // ! deprecated
  VSim = 'hit-sim', // * latest
}

export const ANONYMOUS_HITTER_ID = 'anonymous';
export const ANONYMOUS_HITTER: IHitter = {
  _id: ANONYMOUS_HITTER_ID,
  name: 'Anonymous',
  side: HitterSide.RHH,
  level: PlayerLevel.Pro,
  customID: '',
  _created: '',
  _changed: '',
  _parent_def: '',
  _parent_id: '',
};
